/**
 * This Layout is needed for Starter Kit.
 */
import React, { useLayoutEffect } from "react";
import { Placeholder } from "@sitecore-jss/sitecore-jss-nextjs";
import type { HTMLLink, LayoutServiceData } from "@sitecore-jss/sitecore-jss-nextjs";
import { Box, VisuallyHidden } from "@chakra-ui/react";
import Favicon from "./commons/head/Favicon";
import ReactIconBundle from "./assets/icons/icons.sprite.svg";
import MetaData from "./commons/head/MetaData";
import { HeaderBackgroundProvider } from "utils/contexts/headerBackgroundContext";
import { GTMPageviewTracking } from "commons/head/GTMTracking";
import { transparentize } from "polished";
import ScrollProgress from "commons/ui/ScrollProgress";
import SkipLink from "commons/ui/SkipLink";
import Script from "next/script";
import { getShortLangCode } from "utils/localehelpers";
import Scripts from "./Scrips";
import { SpeedInsights } from "@vercel/speed-insights/next";
import config from "temp/config";
import { setHeaderHeightCssVars } from "utils/headerHeightCssVars";

interface LayoutProps {
    layoutData: LayoutServiceData;
    vercelDomain?: string;
    headLinks: HTMLLink[];
}

const Layout = (props: LayoutProps) => {
    const sitename = props.layoutData.sitecore.context.site?.name;
    const { route, context } = props.layoutData.sitecore;
    const { pageEditing, language } = context;

    // @ts-expect-error: fields.Key.value is not declared in sitecores own types
    const hasWhiteText = route?.fields?.HeaderColor?.fields.Key?.value === "white";
    const countryCode =
        globalThis.document?.cookie &&
        /countryCode=([A-Z]{2})/.exec(globalThis.document.cookie)?.[1];
    const languageCode = getShortLangCode(language);

    const headlessHeaderEmpty = route?.placeholders?.["headless-header"].length === 0;

    useLayoutEffect(() => {
        setHeaderHeightCssVars(!headlessHeaderEmpty);
    }, [headlessHeaderEmpty]);

    return (
        <>
            <Scripts />
            <MetaData
                {...route?.fields}
                MetadataCanonicalUrl={`${!props.vercelDomain?.includes("http") ? "https://" : ""}${
                    props.vercelDomain
                }${props.layoutData.sitecore.context.itemPath as string}`}
            />

            {props.headLinks.map((headLink) => (
                <link rel={headLink.rel} key={headLink.href} href={headLink.href} />
            ))}

            <Favicon />

            {!pageEditing && <GTMPageviewTracking />}

            {/* root placeholder for the app, which we add components to using route data */}
            <Box
                {...(!headlessHeaderEmpty && {
                    pt: [
                        "var(--sizes-headerMobileTopHeight)",
                        null,
                        null,
                        "var(--sizes-headerTopHeight)",
                    ],
                })}
            >
                <SkipLink />
                <ScrollProgress />
                <HeaderBackgroundProvider hasFullHero={hasWhiteText} sitename={sitename ?? ""}>
                    {route && <Placeholder name="headless-header" rendering={route} />}
                </HeaderBackgroundProvider>
                <Box
                    as="main"
                    id="main-content"
                    bg="white"
                    boxShadow={`0px 50px 56px 0px ${transparentize(0.8, "black")}`}
                    position="relative"
                    zIndex={1}
                >
                    {route && <Placeholder name="headless-main" rendering={route} />}
                </Box>

                {route && <Placeholder name="headless-footer" rendering={route} />}
            </Box>

            <VisuallyHidden
                id="icon-sprite"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: ReactIconBundle }}
            />

            {countryCode !== "US" && (
                <Script
                    strategy="afterInteractive"
                    id="CookieConsent"
                    src="https://policy.app.cookieinformation.com/uc.js"
                    data-culture={languageCode}
                    data-gcm-version="2.0"
                    type="text/javascript"
                />
            )}
            <Script
                strategy="afterInteractive"
                type="text/javascript"
                src={"https://view.ceros.com/scroll-proxy.min.js"}
                data-ceros-origin-domains="view.ceros.com"
            />
            <Script
                strategy="afterInteractive"
                type="text/javascript"
                src={"//cdn.bc0a.com/autopilot/f00000000290620/autopilot_sdk.js"}
            />

            {(config.sitecoreSiteName === "SimCorpCom" ||
                config.sitecoreSiteName === "SimcorpGlobalSummitCom") && (
                <SpeedInsights
                    endpoint={`https://www.${
                        config.sitecoreSiteName === "SimCorpCom" ? "simcorp" : "simcorpglobalsummit"
                    }.com/_vercel/speed-insights/vitals`}
                />
            )}
        </>
    );
};

export default Layout;
